import type { LabelledDataSet } from "@/components/types";
import type { NumericPoint } from "@incytestudios/ddv";
import type { XrefPump, XrefPumps } from "@/composables/forms/formTypes";
import { normalizeTornadoPoints } from "@/libs/charting";
import { getDensityCorrectedProducedPoints } from "@/libs/charting/_common/densityCorrection";
import type {
  PumpTornadoChartContext,
  PumpChartDataPointCalculator,
  PumpChartDataPointParams,
  PredictiveCalcs
} from "..";
import { getPressurePerStageHeadFt } from "@/libs/calculations/pumps/pumpData";

export const getProducedTdhDataPoint: PumpChartDataPointCalculator = (
  params: PumpChartDataPointParams
): NumericPoint => {
  const {
    flowRateBblD,
    frequencyRatio,
    gasHeadRerateRatio,
    pumpWearHeadCorrectionRatio,
    ascendingOrderPumps,
    dischargeFluidDensityLBMFT3
  } = params;
  const tdhData = getSimplifiedProducedTdh(
    flowRateBblD,
    frequencyRatio,
    ascendingOrderPumps,
    gasHeadRerateRatio,
    pumpWearHeadCorrectionRatio,
    dischargeFluidDensityLBMFT3
  );
  return {
    x: flowRateBblD,
    y: params.isScenarioEspShore ? tdhData.tdh : tdhData.tdhPressure
  };
};
/**
 * This is a simplified version of the processPumpData() calculations in pumps.ts
 * used for generating the curve on the Tornado Head chart. This runs on a per pump
 * basis instead of a per stage basis. If something changes in processPumpData we
 * need to make changes here as well and vice versa.
 */
export const getSimplifiedProducedTdh = (
  flowRateBblD: number,
  frequencyRatio: number,
  ascendingOrderPumps: XrefPumps,
  gasHeadRerateRatio: number,
  pumpWearHeadCorrectionRatio: number,
  dischargeFluidDensityLBMFT3: number
) => {
  const returnData = ascendingOrderPumps.reduce(
    (a, xrefPump) => {
      const pump = xrefPump.r_pump;
      if (!pump) return a;

      const totalFlowRateCorrectedSTBD =
        (1 / xrefPump.rate_correction_ratio) * (flowRateBblD / frequencyRatio);
      const pumpGraphMax =
        xrefPump.r_pump?.max_graph_rate_standard_pump_bpd || 0;
      // If intake rate exceeds graphMax or is invalid then zero out pressure
      const pumpIntakeFlowRateExceedsGraphMax =
        totalFlowRateCorrectedSTBD > pumpGraphMax;

      const invalidPumpIntakeFlowRate =
        pumpIntakeFlowRateExceedsGraphMax || totalFlowRateCorrectedSTBD <= 0;
      const rateCorrectedPumps = pumpIntakeFlowRateExceedsGraphMax
        ? a.rateCorrectedPumps
        : [...a.rateCorrectedPumps, xrefPump];
      const headFtPerStage = invalidPumpIntakeFlowRate
        ? 0
        : getPressurePerStageHeadFt(
            totalFlowRateCorrectedSTBD,
            xrefPump,
            gasHeadRerateRatio,
            frequencyRatio,
            pumpWearHeadCorrectionRatio
          );
      const headFtPerPump = headFtPerStage * (xrefPump.total_stages || 1);
      const tdh = a.tdh + headFtPerPump;
      return {
        tdh,
        rateCorrectedPumps
      };
    },
    { tdh: 0, rateCorrectedPumps: [] as XrefPump[] }
  );
  return {
    ...returnData,
    tdhPressure: (returnData.tdh / 144.144) * dischargeFluidDensityLBMFT3
  };
};

export const getProducedDataSetsTdh = (
  context: PumpTornadoChartContext,
  predictedCalcsWithDensityCorrectionRatio?: PredictiveCalcs,
  forTaperedPumps: boolean = false
): {
  dataSet: LabelledDataSet;
  adjustedPumpHzFrequency: number;
  originalPumpHzFrequency: number;
  tdhRateCorrectedPumps: XrefPump[];
  predictedCalcsWithDensityCorrectionRatio?: PredictiveCalcs;
}[] => {
  if (!context) return [];
  const {
    pump,
    gasHeadRerateRatio,
    motorPoles,
    baseXUnit,
    targetXUnit,
    targetXUnitLabel,
    baseTdhYUnit,
    targetTdhYUnit,
    targetTdhYUnitLabel,
    hypotheticalPumpFrequenciesHz,
    hypotheticalPumpFrequenciesIncludeRealHz,
    hypotheticalFlowRates,
    pumpWearHeadCorrectionRatio,
    ascendingOrderPumps,
    dischargeFluidDensityLBMFT3,
    isScenarioEspShore
  } = context;

  // Originally we didn't use hypotheticalPumpFrequenciesIncludeRealHz and instead
  // used just hypotheticalPumpFrequencies since it's not needed on the chart. However
  // now we iterate through it for the points so we can find the estimated required tdh point
  // then filter out the dataset afterwards so its not charted
  const frequencySet = forTaperedPumps
    ? hypotheticalPumpFrequenciesHz
    : hypotheticalPumpFrequenciesIncludeRealHz;

  // We're always retrieving the real correction ratio
  const realDensityCorrectionRatios =
    predictedCalcsWithDensityCorrectionRatio?.[context.pumpHz]
      ?.densityCorrectionRatios;
  const realTDHDensityCorrectionRatio =
    realDensityCorrectionRatios?.densityCorrectionTDHRatio;
  const realPressureDensityCorrectionRatio =
    realDensityCorrectionRatios?.densityCorrectionPressureRatio;

  const dataSets = frequencySet.map<{
    dataSet: LabelledDataSet;
    adjustedPumpHzFrequency: number;
    originalPumpHzFrequency: number;
    tdhRateCorrectedPumps: XrefPump[];
  }>(hf => {
    const { frequencyRatio, frequency } = hf;
    const graphMaxBblD =
      (pump?.r_pump?.max_graph_rate_standard_pump_bpd || 0) *
      frequencyRatio *
      pump.rate_correction_ratio;
    const producedTdhData = hypotheticalFlowRates
      .filter(rate => rate < graphMaxBblD)
      .map(flowRateStbD => {
        const tdhDataPoint = getSimplifiedProducedTdh(
          flowRateStbD,
          frequencyRatio,
          ascendingOrderPumps,
          gasHeadRerateRatio,
          pumpWearHeadCorrectionRatio,
          dischargeFluidDensityLBMFT3
        );
        return {
          flowRateStbD,
          tdhDataPoint
        };
      });

    const producedTdhPoints = producedTdhData
      .map<NumericPoint>(tdhDataPointResult => {
        return {
          x: tdhDataPointResult.flowRateStbD,
          y: isScenarioEspShore
            ? tdhDataPointResult.tdhDataPoint.tdh
            : tdhDataPointResult.tdhDataPoint.tdhPressure
        };
      })
      .map<NumericPoint>(pt => ({
        ...pt,
        y: pt.y
      }));

    // Always use corr ratio from real hz, but we could
    // use per hz correction rate if ever needed
    const correctedProducedTdhPoints = getDensityCorrectedProducedPoints(
      producedTdhPoints,
      isScenarioEspShore
        ? realTDHDensityCorrectionRatio
        : realPressureDensityCorrectionRatio
    );
    const adjustedPumpHzFrequency = frequency * (motorPoles / 2);

    // Gather any pumps that needed rate correction for this frequency so we can show
    // the message on the chart if needed
    const tdhRateCorrectedPumps = producedTdhData.reduce(
      (a, producedTdhData) => {
        const existingPumpIds = a.map(p => p.id);
        const pumpsToAdd =
          producedTdhData.tdhDataPoint.rateCorrectedPumps.filter(
            p => existingPumpIds.indexOf(p.id) === -1
          );
        return [...a, ...pumpsToAdd];
      },
      [] as XrefPump[]
    );
    return {
      dataSet: {
        label: `${adjustedPumpHzFrequency}hz`,
        color: "var(--gray-600);",
        title: `${adjustedPumpHzFrequency}hz`,
        xAxisLabel: `(${targetXUnitLabel})`,
        yAxisLabel: `(${targetTdhYUnitLabel})`,
        points: normalizeTornadoPoints(
          correctedProducedTdhPoints,
          baseXUnit,
          targetXUnit,
          baseTdhYUnit,
          targetTdhYUnit
        ),
        originalPoints: correctedProducedTdhPoints
      },
      adjustedPumpHzFrequency,
      originalPumpHzFrequency: frequency,
      tdhRateCorrectedPumps
    };
  });

  return dataSets;
};
